/**
 * The Insights panel's **blocks** — one component per thing the panel says.
 *
 * Split out of `InsightsPanel.tsx` in the 15 Sep clean-up, when the panel grew a stacked
 * composition, a multi-line class trend and a partnership pie and went past 450 lines. The
 * panel file is now the *order* of the blocks and the rules about which dataset type may
 * draw which; this file is what each block looks like.
 *
 * ## One grammar, applied to all seven
 *
 * Client, 15 Sep 2026: *"We need to make it very clean and less congested and clear. Let's
 * space out and distinguish each chart and subtitle and title clear and well aligned and
 * neatly done."* So every block is the same three things in the same order — a **title**
 * (12px/700, ink, sentence case), an optional **subtitle** (10.5px, ink-3, saying what the
 * chart is *of*), and the chart — separated from its neighbour by 18px and a hairline.
 *
 * The old panel's headings were 10px uppercase with letter-spacing, which at that size is a
 * row of grey ticks rather than a word, and every block was 12px from the next with no rule
 * between them, so six blocks read as one long column. Both are fixed in `.ins-*` (base.css)
 * rather than here: the grammar is a stylesheet's job, and a block that opts out of it by
 * carrying its own margin is exactly how the congestion came back last time.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BarsH, ChartFrame, Donut, DonutLegend, LineChart, chartH, chartW } from '../../components/charts';
import type { BarsHSeries } from '../../components/charts/BarsH';
import type { LineSeries } from '../../components/charts/LineChart';
import type { InsightsClass, InsightsHotspots } from '../../lib/analysis';
import { NO_VALUE, fmtCompact, fmtNum } from '../../lib/format';
import type { DonutSlice } from './insightsCharts';

/** The fallback for a class the catalogue gave no colour. */
const NO_COLOR = '#8B958A';

/**
 * The shell every block shares: a title, an optional subtitle, and the content.
 *
 * `id` is the `data-insblock` hook the e2e suite reads to assert the panel's order, so it is
 * required rather than optional — a block with no hook is a block no test can see.
 */
export function Block({
  id, title, sub, extra, titleExtra, children,
}: {
  id: string;
  title: string;
  sub?: string;
  /** Extra `data-` attributes the block carries (`data-insfocus`, …). */
  extra?: Record<string, string>;
  /**
   * Extra `data-` attributes on the **title** rather than the section.
   *
   * The 16 Sep re-skin moved the indicator panel's `<h5>` headings into this shell, and three
   * of their hooks are read as *the heading's own text* (`data-ipobarstitle` is asserted with
   * an exact string). Hanging those off the section would widen them to the whole block —
   * title plus chart plus notes — which is a different assertion wearing the same name.
   */
  titleExtra?: Record<string, string>;
  children: ReactNode;
}) {
  return (
    <section className="ins-block" data-insblock={id} {...extra}>
      <h4 className="ins-title" {...titleExtra}>{title}</h4>
      {sub ? <p className="ins-sub">{sub}</p> : null}
      {children}
    </section>
  );
}

/**
 * The key statistics, as a 2-column grid of tiles.
 *
 * Five numbers in one flex row gave each of them 46 px, which is how `12.4 g/kg` became two
 * lines and the row became four different heights. A grid gives every tile the same box and
 * puts the numbers under each other, so the column of figures is readable as a column.
 */
export interface StatTile {
  label: string;
  /** The figure, formatted with `fmtCompact` unless `text` overrides it. */
  value?: number | null;
  unit?: string;
  /**
   * A figure the caller has already formatted.
   *
   * `fmtCompact` is right for the insights panel, whose tiles are a raster's statistics and
   * whose reader wants the magnitude. The indicator panel's headline is a *quoted* number —
   * "10,578,495 producers" — printed with the catalogue's own `fmt`, and rounding it to
   * "10.6M" in the one place a reader copies it from would be a different claim.
   */
  text?: string;
  /**
   * The figure's colour role. Default is the brand green every other tile uses; `up`/`down`
   * are the §E4 delta's two directions, which are the only figures in the portal whose
   * *sign* is the message.
   */
  tone?: 'up' | 'down';
  /** `data-` hooks on the tile (`data-ipoval`, `data-ipodelta`). */
  extra?: Record<string, string>;
  /**
   * The **headline** tile: the one figure a reader quotes, at 25px over the row.
   *
   * Review round 1 of the 16 Sep re-skin: at the grid's ordinary 15px the indicator's
   * headline printed *smaller* than the by-country bars' own value labels, which inverts the
   * panel's rank — the number the panel is about should not be the smallest number on it.
   * It takes the whole row because a quoted programme total is ten digits and a unit, and
   * half of a 264px grid breaks that across two lines.
   */
  lead?: boolean;
  /** Take the whole row. Set on the last tile of an odd row, so no tile sits beside a gap. */
  wide?: boolean;
}

/** The tone classes, written out rather than assembled — a template literal is invisible to
 *  the orphaned-class check in `styles/css-families.test.ts`. */
const TONE_CLASS: Record<'up' | 'down', string> = { up: 'ins-up', down: 'ins-down' };

export function StatTiles({ tiles }: { tiles: readonly StatTile[] }) {
  return (
    <div className="ins-tiles">
      {tiles.map((tile) => {
        const missing = tile.text === undefined && (tile.value === null || tile.value === undefined);
        return (
          <div
            className={
              tile.lead
                ? 'ins-tile ins-tile--lead'
                : tile.wide
                  ? 'ins-tile ins-tile--wide'
                  : 'ins-tile'
            }
            key={tile.label}
            data-insstat={tile.label}
            {...tile.extra}
          >
            <div className="ins-tile-lab">{tile.label}</div>
            <div className={tile.tone ? `ins-tile-val ${TONE_CLASS[tile.tone]}` : 'ins-tile-val'}>
              {missing ? NO_VALUE : (tile.text ?? fmtCompact(tile.value as number))}
              {!missing && tile.unit ? <small> {tile.unit}</small> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The class composition, as its own bars rather than through `BarsH`.
 *
 * Every class already has a colour — the catalogue's, the one the map is painting it in —
 * so the chart is a swatch, a name and a share, and drawing it as a bar chart would put a
 * shared axis under six rows whose only comparison is to 100%. The legend and the map are
 * reading one class table (`serializers.class_table`).
 *
 * It doubles as the **legend** for the two blocks under it: the stack and the class trend
 * paint the same classes in the same colours, and repeating a swatch list twice more is the
 * congestion the client asked us to remove.
 */
export function ClassBars({ rows }: { rows: readonly InsightsClass[] }) {
  return (
    <div className="ins-classes">
      {rows.map((row) => (
        <div className="ins-class" key={row.value} data-insclass={row.value}>
          <span
            className="ins-class-sw"
            aria-hidden="true"
            style={{ background: row.color ?? NO_COLOR }}
          />
          <span className="ins-class-name" title={row.label}>
            {row.label}
          </span>
          <span className="ins-class-bar" aria-hidden="true">
            <i style={{ width: `${Math.max(1, Math.round(row.pct ?? 0))}%`,
                        background: row.color ?? NO_COLOR }} />
          </span>
          <span className="ins-class-pct">{fmtNum(row.pct, 1)}%</span>
        </div>
      ))}
    </div>
  );
}

/** The stacked composition per country — client item 6. */
export function ClassStack({
  labels, series, width, rowOpacity, ariaLabel,
}: {
  labels: readonly string[];
  series: readonly BarsHSeries[];
  width: number;
  rowOpacity?: readonly number[];
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <ChartFrame
      title={t('insights.classesByCountry')}
      subtitle={t('insights.classesByCountrySub')}
      xLabel={t('insights.axis.areaShare')}
      yLabel={t('charts.axis.country')}
      testId="insights-stack-chart"
    >
      {(size) => (
        <BarsH
          stacked
          labels={labels}
          series={series}
          rowOpacity={rowOpacity}
          w={chartW(size, width)}
          rowH={26}
          labelGutter={74}
          labelSize={9.5}
          fmt={1}
          ariaLabel={ariaLabel}
        />
      )}
    </ChartFrame>
  );
}

/**
 * The multi-line class trend — client item 7.
 *
 * `short: ''` suppresses `LineChart`'s direct end labels on purpose. They are the right
 * answer for two or three series in a 330 px chart; seven class names in the 34 px gutter of
 * a 250 px sidebar overlap into an unreadable stack of words. The classes are named by the
 * swatch list two blocks up, which is in the same colours, and by the hover tip, which names
 * the class and the year it is over.
 *
 * **Every other year is left unlabelled.** Eight epochs across a 216 px plot puts the x
 * ticks 27 px apart, and a four-digit year at 9.5 px is wider than that: `2017` ran into
 * `2018` into `2019` until the axis was one long number. Thinning is counted back from the
 * **last** epoch, so the year the reader is looking at is always the one that is printed.
 * The unlabelled points keep their position and their hover tip, which names the year.
 */
export function ClassTrend({
  labels, series, width, ariaLabel,
}: {
  labels: readonly string[];
  series: readonly LineSeries[];
  width: number;
  ariaLabel: string;
}) {
  // At most five printed years; `every` is 1 while the axis has room for all of them.
  const every = Math.max(1, Math.ceil(labels.length / 5));
  const thinned = labels.map((label, i) =>
    (labels.length - 1 - i) % every === 0 ? label : '',
  );

  const { t } = useTranslation();
  return (
    <ChartFrame
      title={t('insights.classTrend')}
      subtitle={t('insights.classTrendSub')}
      xLabel={t('charts.axis.year')}
      yLabel={t('insights.axis.areaShare')}
      testId="insights-class-trend"
    >
      {(size) => (
        <LineChart
          /* The lightbox has room for every year, so it prints them all. */
          labels={size.expanded ? labels : thinned}
          series={series.map((s) => ({ ...s, short: size.expanded ? s.name : '' }))}
          w={chartW(size, width - 34)}
          h={chartH(size, 150)}
          unit="%"
          fmt={1}
          ariaLabel={ariaLabel}
        />
      )}
    </ChartFrame>
  );
}

/**
 * The hotspot ranking. A ranking, not a threshold: "hotspot" here is "where is this
 * highest", and a cut-off would need a per-dataset value nobody has agreed.
 */
export function HotspotList({
  hotspots,
  decimals = 1,
}: {
  hotspots: InsightsHotspots;
  /** A continuous ranking's precision: the layer's display decimals (Part C). */
  decimals?: number;
}) {
  return (
    <ol className="ins-hotspots">
      {hotspots.rows.map((row) => (
        <li key={row.unit_id} data-inshotspot={row.unit_id}>
          <span className="ins-hs-name" title={row.class_label ?? undefined}>
            {row.name}
          </span>
          <span className="ins-hs-val">
            {fmtNum(row.value, hotspots.stat === 'top_class_share' ? 1 : decimals)}
            {hotspots.stat === 'top_class_share' ? '%' : ''}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * The partnership pie — client item 5 (*"maybe using a pie chart"*).
 *
 * The donut needs a legend and cannot borrow one: a partnership is an org unit, not a map
 * class, so its colour exists nowhere else on the page. The list under the ring is that
 * legend and the numbers at once — each wedge's share, right-aligned and mono, so the
 * column of figures lines up the way the stat tiles' does.
 */
export function PartnershipPie({
  slices, size, centerLabel, centerSub, ariaLabel,
}: {
  slices: readonly DonutSlice[];
  size: number;
  centerLabel: string;
  centerSub: string;
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="ins-pie">
      <ChartFrame
        title={t('insights.partnerships')}
        subtitle={t('insights.partnershipsSub')}
        legend={<DonutLegend items={slices} fmt={1} unit="%" />}
        testId="insights-partnerships"
      >
        {(frame) => (
          <Donut
            items={slices}
            size={frame.expanded ? Math.round(Math.min(frame.w, frame.h) * 0.62) : size}
            centerLabel={centerLabel}
            centerSub={centerSub}
            fmt={1}
            ariaLabel={ariaLabel}
          />
        )}
      </ChartFrame>
      <ul className="ins-legend">
        {slices.map((slice) => (
          <li key={slice.id} data-inspartner={slice.id}>
            <span
              className="ins-class-sw"
              aria-hidden="true"
              style={{ background: slice.color }}
            />
            <span className="ins-class-name" title={slice.label}>
              {slice.label}
            </span>
            <span className="ins-class-pct">{fmtNum(slice.value, 1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
